
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator"
import { Getter, namespace } from "vuex-class"

import _Quill from "quill"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import Guid from "@/scripts/guid"

import SearchDropDown from "@/components/Controls/SearchDropDown.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import { InProgressSaveDetails } from "@/types/inProgressSaveDetails"

const Quill = window.Quill || _Quill

const Link = Quill.import("formats/link")

const caseStore = namespace("cases")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"search-dropdown": SearchDropDown
	}
})
export default class RichTextEditor extends Vue {
	@Ref()
	editorContainer!: HTMLElement

	@Getter isFeatureEnabled!: (featureKey: TenantFeatures) => boolean;

	/**
	 * Value for v-model support
	 */
	@Prop({ type: String, default: "" })
	public value!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean;

	@Prop({ default: () => null })
	public placeholder!: string | null

	@Prop({ default: null })
	public minHeight!: string | null

	@Prop({ type: Boolean, default: false })
	public userHistoryOnly!: boolean

	@Prop({ type: Boolean, default: false })
	public applyMaxHeight!: boolean

	@Prop({ type: Array, default: () => null })
	public placeholders!: { id: string, label: string }[] | null

	@Prop({ type: Boolean, default: false })
	public isPrintTemplate!: boolean;

	@Prop({ type: String, default: "" })
	public id!: string

	@Prop({ type: String, required: false })
	public caseId!: string

	private editor: _Quill | null = null
	private focusWhenReady: boolean = false

	private controlId = Guid.newGuid()
	private currentTextValue: string = ""

	@Prop()
	private caseHeader!: string | null;

	@caseStore.Mutation
	clearInProgressSaveDetails!: () => void

	@caseStore.State
	inProgressSaveDetails!: InProgressSaveDetails

	private caseHeaderEnabled: boolean = this.caseHeader !== null && this.caseHeader !== "";
	private get isCaseHeaderEnabled (): boolean {
		return this.caseHeaderEnabled
	}

	private set isCaseHeaderEnabled (enabled: boolean) {
		this.caseHeaderEnabled = enabled
	}

	@Prop({ default: null })
	private formatFooter!: string | null;
	private hasPageNumbers: boolean = this.formatFooter !== null && this.formatFooter !== "";

	get ToolBarId (): string {
		return `toolbar-${this.controlId}`
	}

	@Watch("value")
	onValueChanged (value: string) {
		if (!this.editor) {
			return
		}

		const editorValue = this.getEditorValue()

		if (editorValue !== value && this.currentTextValue !== value) {
			this.dangerouslyPasteHTMLWithoutFocus(value, "api")
		}
	}

	/**
	 * Pastes the specified html into the quill editor without focusing the editor.
	 * This is required because of a bug in quill causing dangerouslyPasteHTML to steal focus away from whatever else is being currently focused
	 * @see https://github.com/quilljs/quill/issues/2156
	 * @param html The html to paste into the editor
	 * @param source See Quills documentation for source https://quilljs.com/docs/api/#events & https://quilljs.com/docs/modules/clipboard/#dangerouslypastehtml
	 * @private
	 */
	private dangerouslyPasteHTMLWithoutFocus (html: string, source: "api" | "user" | "silent") {
		if (!this.editor) {
			return
		}

		// First - check if what we are receiving is found in the content of what we have
		var tidiedText = html

		// The text should always have a paragraph tag at the end and we don't want to include that in the comparison
		const paragraphTag = "</p>"
		if (tidiedText.endsWith(paragraphTag)) {
			const lengthOfTagToRemove = -4
			tidiedText = tidiedText.slice(0, lengthOfTagToRemove)
		}

		// If the text in the editor contains what we already have we should not set the contents
		if (this.getEditorValue().includes(tidiedText)) {
			return
		}

		// Second - If the above is not true and this is an update triggered by a local save
		// We should not override what the user has as they may have removed content that was no longer necessary
		// Essentially this update is out of sync with what the user has in their editor which takes priority.
		if (this.inProgressSaveDetails &&
				this.id &&
				this.caseId &&
				this.inProgressSaveDetails.caseId === this.caseId &&
				this.inProgressSaveDetails.fieldId === this.id) {
			// We know this is an update triggered by a local save
			if (this.inProgressSaveDetails.text === html) {
				this.clearInProgressSaveDetails()
				return
			}
		}

		// Third - treat this as a valid update to the contents of the editor
		const delta = this.editor!.clipboard.convert(html)
		const range = this.editor!.getSelection()

		// Note: Due to behaviour in Quill using setContents can cause the cursor to move to the beginning of the text box.
		// To avoid this above we have captured the current index the user is at and if available will ensure the cursor is at that location
		var cursorIndex = 0
		if (range) {
			if (range.length === 0) {
				cursorIndex = range.index
			}
		}

		this.editor!.setContents(delta, source)

		// If the new text is smaller than the previous text
		// we want the cursor at the end.
		if (this.editor!.getLength() < cursorIndex) {
			cursorIndex = this.editor!.getLength() - 1
		}

		if (cursorIndex > 0) {
			// Set the cursor location
			this.editor!.setSelection({ index: cursorIndex, length: 1 }, source)
		}
	}

	async mounted () {
		if (this.$refs.editorContainer) {
			const optionalModules = {}

			this.editor = new Quill(this.editorContainer, {
				placeholder: this.placeholder || "",
				readOnly: this.readonly,
				modules: {
					toolbar: `#${this.ToolBarId}`,
					magicUrl: true,
					history: {
						userOnly: this.userHistoryOnly
					},
					...optionalModules
				},
				formats: [
					"bold",
					"italic",
					"link",
					"indent",
					"header",
					"list",
					"underline",
					"strike",
					"placeholder",
					"image"
				]
			})

			this.editor!.on("text-change", () => {
				const newValue = this.getEditorValue()
				if (newValue !== this.value) {
					this.currentTextValue = newValue
					this.$emit("input", newValue)
				}
			})

			this.editor!.on("selection-change", (range, oldRange, source) => {
				if (range == null) return
				if (range.length === 0 && source === "user") {
					// Find link element using range
					const [link, _offset] = (this.editor!.scroll as any).descendant(
						Link,
						range.index
					)
					// navigate to the URl in a new tab if the dom node is of type link
					if (link && link.domNode && link.domNode.href) {
						window.open(link.domNode.href, "_blank", "noopener,noreferrer")
					}
				}
			})

			if (this.value) {
				this.dangerouslyPasteHTMLWithoutFocus(this.value, "api")
			} else {
				this.dangerouslyPasteHTMLWithoutFocus("", "api")
			}
		}
	}

	beforeDestroy () {
		if (this.editor) {
			this.editor = null
		}
	}

	@Watch("readonly")
	onReadonlyChanged (readonly: boolean) {
		if (this.editor) {
			this.editor.enable(!readonly)
			if (this.focusWhenReady && !readonly) {
				this.focusWhenReady = false
				this.editor.focus()
			}
		}
	}

	focusOut (event: FocusEvent) {
		// Ensure that this event isn't triggered when changing between children, e.g. clicking a button while focused on text
		if (!event.relatedTarget || !this.$el.contains(event.relatedTarget as Element)) {
			this.$emit("blur")
		}
	}

	clickAndFocus () {
		if (this.readonly) {
			this.focusWhenReady = true
			this.$emit("focus")
		}
	}

	private getEditorValue (): string {
		return this.editorContainer.children.length === 0 ? "" : this.editorContainer.children[0].innerHTML
	}

	private get minHeightStyleString (): string {
		return `min-height: ${this.minHeight};`
	}

	/**
	 * Adds the selected placeholder at the cursor location
	 * @param placeholder The placeholder to add, with the id being the field id or built in property name and the label being what is displayed to the users.
	 * @private
	 */
	private addPlaceholder (placeholder: { id: string, label: string }) {
		const selection = this.editor!.getSelection(true)!

		const insertIndex = selection ? selection.index : this.editor!.getLength()
		this.editor!.insertEmbed(insertIndex,
			"placeholder",
			placeholder,
			"user")

		setTimeout(() => {
            this.editor!.setSelection(insertIndex + 1, 0, "user")
            this.editor!.focus()
		}, 1)
	}

	private onIncludeHeaderClick () : void {
		this.isCaseHeaderEnabled = !this.isCaseHeaderEnabled
		this.$emit("includeCaseHeaderChange", this.isCaseHeaderEnabled)
	}

	private onUsePageNumbersClick (): void {
		this.hasPageNumbers = !this.hasPageNumbers
		this.$emit("setUsePageNumbers", this.hasPageNumbers)
	}

	private get isPdfTemplateHeaderEnabled () {
		return this.isFeatureEnabled(TenantFeatures.PdfTemplateHeaderAndFooter)
	}
}
